/**
 * SimSpec.js - a class to manage data for a fragment simulation run
 */

import { Polymer } from './atomgroups';
import { MapCase } from './MapCase';
import { sameStructure } from './structureRef';
import { leadingPdbId } from '../util/pdb_id';

/**
 * @typedef {import('./atomgroups').AtomGroup} AtomGroup
 */

export class SimSpec {
    constructor(obj) {
        /**
         * @type {MapCase} The structure the simulation runs on. Supplies project / case / pdbID
         * for the default names, and `sourceDesc`, which decides whether fragserv routes the run
         * as `cp` (structures from CommonStructuresDir) or `user` (from the personal store).
         */
        this.protein = obj.protein || null;
        /** @type {Polymer[]} Protein chains included in the simulation. */
        this.proteinChains = obj.proteinChains || null;
        /**
         * @type {AtomGroup?} The compound or ligand to include, if any. Its name is appended to
         * the default case name as `+<name>`, which is why a compound run never collides.
         */
        this.compound = obj.compound || null;
        /** @type {AtomGroup[]} Ions to include. */
        this.ions = obj.ions || [];
        /** @type {AtomGroup[]} Cofactors to include. */
        this.cofactors = obj.cofactors || [];
        /** @type {Polymer[]} Non-protein polymers (e.g. peptide chains) to include. */
        this.polymers = obj.polymers || [];
        /** @type {AtomGroup[]} Objects explicitly excluded from `selectQuery`. */
        this.ignoredObjects = obj.ignoredObjects || [];
        /** @type {string} The fragserv project ("Target Protein Name" in the form). */
        this.project = obj.project || SimSpec.defaultProjectName(this);
        /**
         * @type {string} The fragserv case ("Structure Name" in the form). Together with
         * `project` this is the structure's identity; duplicating an existing project/case is
         * what BP2-2680 guards against.
         */
        this.case = obj.case || SimSpec.defaultCaseName(this);
        /**
         * @type {string} Custom binding-site specification. `write-project-case` writes it
         * into the case (the checkpoint and the <case>_binding-site-specs file the
         * simulation specs include), with a location bias toward each site, so it is one of
         * the two things (with minimize-box) lost when a run is misrouted to an existing
         * structure.
         */
        this.bindingSiteSpecs = obj.bindingSiteSpecs || '';
        /**
         * @type {boolean} Without custom specs, whether bfd-server derives binding sites
         * (and their location bias) from the simulation's compounds; false for none, a
         * broader search.
         */
        this.autoBindingSite = obj.autoBindingSite ?? true;
        /** @type {string} Fragment specification sent to fragserv. */
        this.fragments = obj.fragments || '';
    }

    get selectQuery() {
        const chains = [...this.proteinChains, ...this.polymers];
        let including = `${chains.map((x) => `chain ${x.chain}`).join(' and not ligand or ')} and not ligand`;
        if (this.compound) including += ` or ${this.compound.selectQuery}`;
        this.ions.forEach((x) => { including += ` or ${x.resSpec}`; });
        this.cofactors.forEach((x) => { including += ` or ${x.resSpec}`; });

        let excluding = [];
        for (const ignored of this.ignoredObjects) {
            if (ignored !== this.compound) {
                const spec = ignored instanceof Polymer
                    ? `chain ${ignored.chain}`
                    : ignored.resSpec;
                excluding.push(spec);
            }
        }
        const selectHack = 'chain impossible or '; // Workaround for select issue
        excluding = excluding.length > 0 ? `and not (${selectHack}${excluding.join(' or ')})` : '';
        const query = `(${including}) ${excluding}`;
        console.log(`Select query for prepare simulation data:\n${query}`);
        return query;
    }

    get forServer() {
        return {
            project: this.project,
            case: this.case,
            bindingSiteSpecs: this.bindingSiteSpecs,
            autoBindingSite: this.autoBindingSite,
        };
    }

    get forFragserv() {
        const protein = this.protein;
        const structureLabel = protein.pdbID?`Specified ${protein.pdbID}`:'Custom';
        let description = `${structureLabel} structure`;
        if (this.compound) {
            description += ` with ${this.compound.isLigand() ? 'ligand' : 'compound'} ${this.compound.resSpec}`;
        }

        // Fragserv will use the sourceDesc field to know where to find the structures.
        // If we are writing to the personal directory rather than reusing the structure as it
        // stands, change the sourceDesc so fragserv doesn't look in the default place for that
        // protein.
        //
        // bfd-server writes the prepared case to <personal>/structures/<project>/<case>, so
        // EITHER part differing means a new location. This used to compare the case only, which
        // left a hole: keeping the case name while changing the project produced an unmodified
        // sourceDesc, so fragserv classified the run `cp` and looked in CommonStructuresDir for
        // a project that does not exist there -- a FileNotFoundError at launch.py:442, raised
        // after the user has already been billed and their cart emptied. See BP2-2680.
        let sourceDesc = '';
        if (protein) {
            sourceDesc = protein.sourceDesc;
            const writingNewLocation = protein.project !== this.project
                || protein.case !== this.case;
            if (writingNewLocation && !protein.sourceDesc.endsWith('_modified')) {
                sourceDesc += '_modified';
            }
        }

        return {
            project: this.project || '',
            case: this.case || '',
            fragments: this.fragments || '',
            gene_name: protein ? protein.gene_name : '',
            molecule_name: protein ? protein.molecule_name : '',
            pdbid: protein ? protein.pdbID : '',
            description,
            sourceDesc,
        };
    }

    /**
     * POST arguments that will be sent to fragserv
     */
    static fragservArgsFromProtein(protein, fragments='') {
        return {
            project: protein.project || '',
            case: protein.case || '',
            fragments,
            gene_name: protein ? protein.gene_name : '',
            molecule_name: protein ? protein.molecule_name : '',
            pdbid: protein ? protein.pdbID : '',
            description: protein ? protein.description : '',
            sourceDesc: protein ? protein.sourceDesc : '',
        };
    }

    static defaultProjectName({ protein }) {
        if (!protein) return '';
        const name = protein.project || protein.gene_name || protein.molecule_name || '';
        return name.trim().replace(/[^\w]/g, '-');
    }

    // This default case will populate the "structure name" field when creating a
    // new simulation. `simType` ('water' | 'fragment' | 'clustering') is optional; it is used
    // only to name the simulation type when the default would otherwise duplicate the
    // structure's own case name.
    static defaultCaseName({ protein, compound, simType }) {
        if (!protein) return '';
        let caseName = '';
        let cmpdName = '';
        if (compound) {
            cmpdName = compound.isLigand() ? compound.resname : compound.resSpec;
        }

        // The default case name has a protein part and a compound part.
        // Protein part could be pdbid or derived from the case name, depending on source.
        // The logic for when to include the compound part changes by source.

        // Most scenarios use pdbID for default case name. If empty, the user must specify.
        caseName = protein.pdbID;

        switch (protein.sourceDesc) {
            case MapCase.Sources.Public:
                // If there ever is an installed bmaps case without a pdbid,
                // use its leading PDB id or else the first portion of the case name
                if (!caseName) caseName = leadingPdbId(protein.case) || protein.case.split('_')[0];
                break;
            case MapCase.Sources.Fragserv:
                caseName = protein.case;
                // For fragserv and other cases, keep the existing case name, adding the
                // compound if it's not already present.
                // This may result in case names like "PDBID+cmpd_1+cmpd_2" if you run a
                // water simulation on one compound and then another on the results of the first.
                // Not going to try to be clever at parsing case components from the name.
                // Don't allow characters except separators after the cmpdName
                if (cmpdName && !caseName.match(`\\+${cmpdName}([-_\\.\\+]|$)`)) {
                    caseName += `+${cmpdName}`;
                    cmpdName = ''; // Prevent appending cmpdName a 2nd time below.
                }
                break;
            // No default - handled above (pdbID)
        }

        // A default that reproduces the structure's OWN case name is a trap, whatever the
        // source. bfd-server's validate-project-case only enumerates the user's structures
        // directory, so it answers NEW and the client writes a prepared case into the user's
        // store -- while forFragserv leaves sourceDesc unchanged (it only appends `_modified`
        // when the case names differ), so fragserv classifies the run as `cp` and launches the
        // BUILT-IN files. The values baked into the checkpoint at prepare time -- binding-site
        // specs and minimize-box -- are then silently replaced by the original's.
        //
        // Name the simulation type rather than bumping a counter: waters and hotspots really do
        // belong in their own cases (the UI already expects that), so `_waters` / `_frags` /
        // `_hotspots` describes a genuine distinction and gives the user something navigable,
        // instead of an undifferentiated `_1` / `_2` / `_3` stack. Falls back to the mechanical
        // variant when the caller does not know the simulation type.
        // Only reachable with no compound: a compound already appends `+<cmpd>` just below.
        if (caseName && !cmpdName && caseName === protein.case) {
            const suffix = SimSpec.simTypeSuffix(simType);
            caseName = suffix ? `${caseName}${suffix}` : SimSpec.caseNameVariant(caseName);
        }

        // Don't add compound to empty caseName, which would make, eg. "+4K6"
        if (caseName && cmpdName) {
            caseName += `+${cmpdName}`;
        }

        return caseName;
    }

    /**
     * The case-name suffix naming a simulation type. Waters and hotspots belong in their own
     * cases -- the UI already treats them that way -- so saying which kind of run a case holds
     * is describing a real distinction, not smuggling metadata into a name.
     *
     * Scope note: this is for the simulation TYPE only. It is not a general channel for
     * structure or run metadata (binding-site presence, Gen2/FFA, and so on); that belongs on
     * the structure -- see BP2-2662.
     * @param {string} [simType] 'water' | 'fragment' | 'clustering'
     * @returns {string} the suffix, or '' when the type is unknown
     */
    static simTypeSuffix(simType) {
        return { water: '_waters', fragment: '_frags', clustering: '_hotspots' }[simType] || '';
    }

    /**
     * Is `caseName` one the form generated, rather than one the user typed?
     *
     * The form derives the name two different ways, and both count as generated:
     * `deriveState` seeds it as the bare default, while the input handlers append the
     * modification tag (`getCaseNameTag`). Those diverge the moment anything is hidden --
     * `GetFromWorkspace` filters chains/ions by visibility while formOptions holds all of them,
     * so one hidden chain makes the tag non-empty on first open while the seeded name still has
     * none. Comparing against only the tagged form would then classify a freshly generated name
     * as user-typed, and leave it stale.
     *
     * Pure and exported for testing. Tests in test/bmaps/simSpecCaseNames.test.js.
     * @param {string} caseName the current value
     * @param {string} base the bare default for the relevant simulation type
     * @param {string} [tag] the modification tag that may or may not have been appended
     * @returns {boolean}
     */
    static isGeneratedCaseName(caseName, base, tag='') {
        if (!base) return false;
        return caseName === base || caseName === `${base}${tag}`;
    }

    /**
     * A mechanical variant of a case name, mirroring bfd-server's
     * ProjectCaseManager::getUniqueCaseName: strip a trailing `_<digits>`, then append `_<n>`.
     * The digits of a leading PDB id are not a suffix (pdb_00001914 -> pdb_00001914_1).
     * Sharing the server's shape keeps one disambiguation convention rather than two.
     *
     * Used to suggest a free name when one is already taken, and as the fallback when the
     * simulation type is unknown. Unlike `simTypeSuffix` this says nothing about the case --
     * it exists only to make the name differ from one already in use.
     * @param {string} caseName
     * @param {number} [n]
     * @returns {string}
     */
    static caseNameVariant(caseName, n=1) {
        const idLength = leadingPdbId(caseName).length;
        const base = caseName.slice(0, idLength) + caseName.slice(idLength).replace(/_\d+$/, '');
        return `${base}_${n}`;
    }

    /**
     * Does this spec's project/case collide with a structure the client already knows about?
     *
     * bfd-server's validate-project-case enumerates only the user's own structures directory,
     * so a name matching a built-in, curated or otherwise-mounted structure passes its check.
     * This closes that gap client-side, across every mounted data source.
     *
     * Caveat: it sees MOUNTED data sources only, so it is a strong guard, not a guarantee --
     * the server-side check remains the backstop.
     *
     * Pure and exported for testing. Tests in test/bmaps/simSpecCaseNames.test.js.
     * @param {SimSpec} simSpec the spec whose project/case is being proposed
     * @param {MapCase[]} [existingMapCases] every MapCase the client currently knows about
     * @returns {{collidesWith: MapCase, suggestion: string, isSourceStructure: boolean}?}
     *   null when there is no collision
     */
    static findCaseNameCollision(simSpec, existingMapCases=[]) {
        const { project, case: caseName, protein } = simSpec;
        if (!project || !caseName) return null;

        // Compare project and case directly rather than deriving a key: both fields are already
        // per-structure -- the mount index lives only in `uri` -- so this is mount-free by
        // construction, and it sidesteps the empty-projectCase gotcha that affects scheme
        // imports (see structureRef.js).
        //
        // Indexed in one pass rather than rescanned per candidate: the suggestion loop below
        // tries up to 1000 names, and the map list can be large.
        const taken = new Map();
        for (const mapCase of existingMapCases) {
            if (mapCase.project !== project) continue;
            // When several mounts carry the same project/case, prefer the structure being
            // simulated FROM, so isSourceStructure -- and the reuse hint it drives -- does not
            // depend on the order the map lists happen to be enumerated in.
            if (!taken.has(mapCase.case) || sameStructure(protein, mapCase)) {
                taken.set(mapCase.case, mapCase);
            }
        }

        const collidesWith = taken.get(caseName);
        if (!collidesWith) return null;

        let suggestion = '';
        for (let i = 1; i <= 1000; i += 1) {
            const candidate = SimSpec.caseNameVariant(caseName, i);
            if (!taken.has(candidate)) {
                suggestion = candidate;
                break;
            }
        }

        // Both sides are real MapCases here, so structure identity is sameStructure's job.
        // This distinguishes the BP2-2680 case -- colliding with the structure being simulated
        // FROM -- which is the only situation where reusing the existing setup is an option.
        return {
            collidesWith,
            suggestion,
            isSourceStructure: sameStructure(protein, collidesWith),
        };
    }

    /**
     * Derive a default SimSpec from a CaseData and workspace.
     * The caseData is the source of the data.
     * The workspace contains the active compound as well as (in)visibility information that
     * may be used (not currently) to uncheck some of the protein chains by default.
     *
     * @param {Workspace} workspace The Workspace containing active / visible metadata
     * @param {CaseData} caseData The CaseData containing structure data for the simulation
     * @param {boolean} includeActiveCompound Whether to include Workspace active cmpd in the sim.
     * @param {string} fragments
     * @returns {SimSpec} The default SimSpec for this Workspace and CaseData
     *
     * Note: I went back and forth about how to handle the compound argument.
     * Should it become a compound object reference and the caller has to decide which one to send?
     * If the caseData doesn't have the currently active compound, should we try a ligand instead?
     * I decided to keep to keep the behavior the same, using a flag for whether or not to include
     * the active compound, and I decided to only apply it to the active compound.
     * So if the user has an active compound from one protein and they attempt a simulation on a
     * different protein, by default the derived SimSpec will not have a cmpd;
     * the user will have to specify.
     *
     */
    static GetFromWorkspace(workspace, caseData, includeActiveCompound=false, fragments='') {
        if (!caseData) return new SimSpec();

        let compound;
        if (includeActiveCompound) {
            const activeCmpd = workspace.getActiveCompound();
            if (activeCmpd && caseData.hasAtomGroup(activeCmpd)) {
                compound = activeCmpd;
            }
        }

        const polymerChains = caseData.getPolymers();
        let ions = caseData.getIons();
        let cofactors = caseData.getCofactors();
        // Filter out polymer ions and cofactors
        if (polymerChains.length > 0) {
            const polymerChainIds = polymerChains.map((i) => i.chain);
            ions = ions.filter((i) => !polymerChainIds.includes(i.chain));
            cofactors = cofactors.filter((i) => !polymerChainIds.includes(i.chain));
        }

        // First filter out any invisible ions, cofactors, and chains
        ions = ions.filter((i) => workspace.isVisible(i));
        cofactors = cofactors.filter((c) => workspace.isVisible(c));
        const proteinChains = caseData.getProteinChains();
        const proteinChainIds = proteinChains.map((c) => c.chain);
        const visibleChains = proteinChains.filter((p) => workspace.isVisible(p));
        const visibleChainIds = visibleChains.map((c) => c.chain);
        // Include cofactors and ions that are on visible chains or independent
        const independentIons = ions.filter((i) => !proteinChainIds.includes(i.chain));
        ions = ions.filter((i) => visibleChainIds.includes(i.chain));
        ions = [...ions, ...independentIons];
        const independentCofactors = cofactors.filter((c) => !proteinChainIds.includes(c.chain));
        cofactors = cofactors.filter((c) => visibleChainIds.includes(c.chain));
        cofactors = [...cofactors, ...independentCofactors];

        return new SimSpec({
            protein: caseData.mapCase,
            proteinChains: visibleChains,
            compound: compound || null,
            ions,
            cofactors,
            fragments,
        });
    }
}

SimSpec.CaseStatus = {
    NEW: 'NEW',
    EXISTS_OK: 'EXISTS_OK',
    EXISTS_CONFLICT: 'EXISTS_CONFLICT',
};

SimSpec.Status = {
    Ready: 'Ready',
    Conflict: 'Conflict',
    StorageUpdatedError: 'StorageUpdatedError',
    PersonalStorageError: 'PersonalStorageError',
    NoHydrogensError: 'NoHydrogensError',
    FdbFileError: 'FdbFileError',
    PartialChargesError: 'PartialChargesError',
    UnknownError: 'UnknownError',
    TooManyChainsError: 'TooManyChainsError',
    BindingSiteSpecError: 'BindingSiteSpecError',
    NoProteinChainsError: 'NoProteinChainsError',
};
